import { useEffect, useState } from 'react';
import api, { Attestation } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg } from '../utils';

export default function AttestationPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('iga-admin');
  const canDecide = hasRole('iga-admin', 'iga-reviewer');

  const [attestations, setAttestations] = useState<Attestation[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [subjectUserId, setSubjectUserId] = useState('');
  const [subjectUsername, setSubjectUsername] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const r = await api.get<Attestation[]>('/attestations');
      setAttestations(r.data);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async () => {
    setError('');
    try {
      await api.post('/attestations', { subjectUserId, subjectUsername });
      setSubjectUserId('');
      setSubjectUsername('');
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const decide = async (id: string, decision: 'attested' | 'flagged') => {
    try {
      await api.post(`/attestations/${id}/decision`, { decision });
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const current = attestations.find((a) => a.id === selected) ?? null;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Attestation</h2>
      {error && <p className="text-coral">{error}</p>}

      {isAdmin && (
        <section className="card flex items-end gap-3">
          <div className="flex-1">
            <label className="label">User-ID</label>
            <input
              className="input"
              placeholder="Keycloak User-ID"
              value={subjectUserId}
              onChange={(e) => setSubjectUserId(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="label">Benutzername</label>
            <input
              className="input"
              placeholder="max.mustermann"
              value={subjectUsername}
              onChange={(e) => setSubjectUsername(e.target.value)}
            />
          </div>
          <button
            className="btn-primary"
            disabled={!subjectUserId || !subjectUsername}
            onClick={create}
          >
            Anfordern
          </button>
        </section>
      )}

      <div className="grid grid-cols-3 gap-6">
        <section className="card col-span-1 space-y-2">
          <h3 className="font-medium text-coral">Attestierungen</h3>
          {attestations.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a.id)}
              className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                selected === a.id ? 'bg-coral text-black' : 'hover:bg-ink'
              }`}
            >
              <div className="font-medium">{a.username}</div>
              <div className="text-xs opacity-70">
                {a.status} · {a.snapshot.length} Rechte
              </div>
            </button>
          ))}
          {attestations.length === 0 && (
            <p className="text-sm text-gray-500">Keine Attestierungen.</p>
          )}
        </section>

        <section className="card col-span-2 overflow-x-auto">
          {!current ? (
            <p className="text-sm text-gray-500">Attestierung wählen.</p>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm text-gray-300">
                  <span className="font-medium text-white">{current.username}</span> · Status:{' '}
                  {current.status}
                </div>
                {canDecide && current.status === 'pending' && (
                  <div className="flex gap-2">
                    <button className="btn-primary" onClick={() => decide(current.id, 'attested')}>
                      Bestätigen
                    </button>
                    <button className="btn-ghost" onClick={() => decide(current.id, 'flagged')}>
                      Beanstanden
                    </button>
                  </div>
                )}
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Typ</th>
                    <th className="th">Berechtigung</th>
                    <th className="th">Dienst</th>
                    <th className="th">Quelle</th>
                  </tr>
                </thead>
                <tbody>
                  {current.snapshot.map((e, i) => (
                    <tr key={`${e.kind}:${e.name}:${i}`} className="border-b border-edge/50">
                      <td className="td">{e.kind}</td>
                      <td className="td">{e.name}</td>
                      <td className="td">{e.service}</td>
                      <td className="td">{e.source}</td>
                    </tr>
                  ))}
                  {current.snapshot.length === 0 && (
                    <tr>
                      <td className="td text-gray-500" colSpan={4}>
                        Keine aktiven Berechtigungen.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
